<template>
  <div class="login-page">
    <!-- Left Panel — Form -->
    <div class="login-panel">
      <div class="login-panel__inner">
        <!-- Header: Envizion Logo -->
        <div class="login-panel__header">
          <img
            :src="avaLogoSrc"
            alt="Envizion"
            class="login-panel__logo"
          />
        </div>

        <!-- Form Area -->
        <div class="login-panel__form">
          <login-form
            :login-form="loginForm"
            :loading="loading"
            :remember-me="isRememberMeChecked"
            :form-rules="formRules"
            :initial-email="initialEmail"
            :auto-check-email="hasEmailInUrl"
            @update:rememberMe="isRememberMeChecked = $event"
            @email-checked="onEmailChecked"
            @step-changed="onStepChanged"
            @submit="login"
          />
        </div>

        <!-- Footer -->
        <div class="login-panel__footer">
          <div v-if="techByLogoSrc" class="login-panel__tech-by">
            <img :src="techByLogoSrc" alt="Technology by Envizion" />
          </div>
          <span class="login-panel__copyright">
            &copy; {{ currentYear }} Envizion
          </span>
        </div>
      </div>
    </div>

    <!-- Right Panel — Image -->
    <div
      class="login-image"
      :style="imageStyle"
    >
      <!-- Client Logo Overlay -->
      <div v-if="customUI && customUI.logoUrl" class="login-image__client-logo">
        <img :src="customUI.logoUrl" alt="Client logo" />
      </div>

      <!-- Default branding when no custom image -->
      <div v-if="!customLoginBackgroundImageUrl" class="login-image__branding">
        <img
          src="/img/brand/Envizion_Brandmark_Emerald.svg"
          alt=""
          class="login-image__brandmark"
        />
        <div class="login-image__tagline">
          <span class="login-image__tagline-label">Asset Visibility</span>
          <span class="login-image__tagline-separator">&mdash;</span>
          <span class="login-image__tagline-label">Anywhere</span>
        </div>
      </div>
    </div>

    <!-- Loading overlay while checking custom UI -->
    <v-overlay :value="customUILoading" absolute>
      <envizion-loader :size="64" />
    </v-overlay>
  </div>
</template>

<script>
  import { error } from '@/utils/debugLogger';
  import formRules from '@Share/config/form-rules';
  import { mapGetters, mapState } from 'vuex';
  import { LogoVariant } from '@Share/config/enums.js';
  import LoginForm from '@Account/components/LoginForm.vue';

  export default {
    name: 'login-page',
    components: { LoginForm },
    data() {
      return {
        formRules,
        loginForm: {
          email: '',
          password: '',
        },
        initialEmail: '',
        hasEmailInUrl: false,
      };
    },
    computed: {
      ...mapState('ACCOUNT', ['loading']),
      ...mapGetters('ACCOUNT', ['GET_REMEMBER_ME', 'GET_CUSTOM_UI', 'GET_CUSTOM_UI_LOADING']),
      customUI() {
        return this.GET_CUSTOM_UI;
      },
      customLoginBackgroundImageUrl() {
        return this.customUI && this.customUI.loginBackgroundImageUrl
          ? this.customUI.loginBackgroundImageUrl
          : null;
      },
      customUILoading() {
        return this.GET_CUSTOM_UI_LOADING;
      },
      isRememberMeChecked: {
        get() {
          return this.$store.getters['ACCOUNT/GET_REMEMBER_ME'];
        },
        set(val) {
          this.$store.commit('ACCOUNT/SET_REMEMBER_ME', val);
        },
      },
      currentYear() {
        return new Date().getFullYear();
      },
      imageStyle() {
        if (this.customLoginBackgroundImageUrl) {
          // Quote the URL so CSS url() parsing survives filenames containing
          // parentheses, spaces, commas, or other characters that would
          // otherwise terminate the url() value prematurely (same fix that
          // was applied to the Facilities top-section banner).
          return {
            backgroundImage: `url("${this.customLoginBackgroundImageUrl}")`,
          };
        }
        return {};
      },
      avaLogoSrc() {
        return '/img/brand/Envizion_Logo_CastletonGreen.svg';
      },
      techByLogoSrc() {
        if (!this.customUI || !this.customUI.techByLogoType) {
          return null;
        }

        switch (this.customUI.techByLogoType) {
          case LogoVariant.WHITE:
            return '/img/White - Technology By Logo.png';
          case LogoVariant.BLACK:
            return '/img/Black - Technology By Logo.png';
          case LogoVariant.COLORED:
          default:
            return '/img/Black - Technology By Logo.png';
        }
      },
    },
    methods: {
      login() {
        this.$store.dispatch('ACCOUNT/LOGIN', this.loginForm);
      },
      async checkCustomUI() {
        const baseUrl = `${window.location.protocol}//${window.location.host}/`;
        try {
          await this.$store.dispatch('ACCOUNT/CHECK_CUSTOM_UI', baseUrl);
        } catch (err) {
          error('Error checking custom UI:', err);
        }
      },
      checkEmailFromUrl() {
        const urlParams = new URLSearchParams(window.location.search);
        const emailParam = urlParams.get('email');

        if (emailParam) {
          this.initialEmail = decodeURIComponent(emailParam);
          this.hasEmailInUrl = true;
        }
      },
      onEmailChecked({ email }) {
        if (email) {
          const url = new URL(window.location.href);
          url.searchParams.set('email', encodeURIComponent(email));
          window.history.replaceState({}, document.title, url.toString());
          this.loginForm.email = email;
        }
      },
      onStepChanged(step) {
        if (step === 1) {
          const url = new URL(window.location.href);
          url.searchParams.delete('email');
          window.history.replaceState({}, document.title, url.toString());
        }
      },
    },
    async mounted() {
      this.checkEmailFromUrl();
      await this.checkCustomUI();
    },
  };
</script>

<style lang="scss" scoped>
  .login-page {
    display: flex;
    height: 100vh;
    overflow: hidden;
    background: #fafafa;
  }

  // =========================================================================
  // Left Panel — Form
  // =========================================================================
  .login-panel {
    width: 480px;
    min-width: 480px;
    height: 100vh;
    background: #ffffff;
    display: flex;
    align-items: center;
    justify-content: center;
    position: relative;
    z-index: 2;
    border-right: 1px solid rgba(0, 0, 0, 0.06);

    @media (max-width: 1100px) {
      width: 440px;
      min-width: 440px;
    }

    @media (max-width: 800px) {
      width: 100%;
      min-width: 100%;
      border-right: none;
    }

    &__inner {
      width: 100%;
      max-width: 420px;
      padding: 56px 40px;
      display: flex;
      flex-direction: column;
      min-height: 100vh;
    }

    &__header {
      padding-bottom: 56px;
    }

    &__logo {
      height: 36px;
      display: block;
    }

    &__form {
      flex: 1;
      display: flex;
      flex-direction: column;
      justify-content: center;
    }

    &__footer {
      padding-top: 32px;
      display: flex;
      align-items: center;
      justify-content: space-between;
    }

    &__tech-by {
      img {
        height: 20px;
        opacity: 0.5;
      }
    }

    &__copyright {
      font-size: 12px;
      color: #9e9e9e;
      letter-spacing: 0.02em;
    }
  }

  // =========================================================================
  // Right Panel — Image
  // =========================================================================
  .login-image {
    flex: 1;
    height: 100vh;
    background-color: #03624c;
    background-size: cover;
    background-position: center;
    background-repeat: no-repeat;
    position: relative;
    display: flex;
    align-items: center;
    justify-content: center;

    @media (max-width: 800px) {
      display: none;
    }

    // Mint edge line — visual bridge between panels
    &::after {
      content: '';
      position: absolute;
      left: 0;
      top: 0;
      bottom: 0;
      width: 1px;
      background: linear-gradient(
        to bottom,
        transparent 0%,
        rgba(44, 194, 149, 0.3) 40%,
        rgba(44, 194, 149, 0.3) 60%,
        transparent 100%
      );
      z-index: 1;
    }

    &__client-logo {
      position: absolute;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      z-index: 2;

      img {
        max-width: min(400px, 60%);
        max-height: 120px;
        object-fit: contain;
      }
    }

    &__branding {
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: 32px;
      z-index: 1;
    }

    &__brandmark {
      width: 96px;
      height: auto;
      opacity: 0.22;
    }

    &__tagline {
      display: flex;
      align-items: center;
      gap: 12px;
      font-size: 11px;
      letter-spacing: 0.28em;
      text-transform: uppercase;
      color: rgba(255, 255, 255, 0.75);
    }

    &__tagline-separator {
      opacity: 0.3;
    }
  }

  // =========================================================================
  // LoginForm Overrides (scoped deep)
  // =========================================================================
  ::v-deep {
    .display-1 {
      font-family: 'Popins', 'Roboto', sans-serif !important;
      font-size: 26px !important;
      letter-spacing: -0.02em;
      color: #1a1a1a;
      line-height: 1.2;
    }

    .title {
      font-family: 'IBM Plex Sans', 'Roboto', sans-serif !important;
      font-size: 14px !important;
      font-weight: 400;
      color: #727272;
    }

    // Input fields — taller, more professional
    .v-text-field--outlined .v-input__slot {
      min-height: 48px;
    }

    .v-text-field--outlined fieldset {
      border-color: #e0e0e0;
      transition: border-color 0.2s ease, box-shadow 0.2s ease;
    }

    .v-text-field--outlined:hover fieldset {
      border-color: #03624c;
    }

    .v-text-field--outlined.v-input--is-focused {
      .v-input__slot {
        box-shadow: -2px 0 0 0 #2cc295;
      }
      fieldset {
        border-color: #03624c;
        border-width: 1.5px;
      }
    }

    // Labels — uppercase spec-like feel
    .v-label {
      font-family: 'IBM Plex Sans', 'Roboto', sans-serif;
      font-size: 12px;
      font-weight: 500;
      color: #424242;
      letter-spacing: 0.03em;
      text-transform: uppercase;
    }

    .v-btn {
      font-family: 'IBM Plex Sans', 'Roboto', sans-serif;
      letter-spacing: 0.02em;
    }

    // Disabled button — branded faded green instead of grey
    .v-btn.v-btn--disabled.login-btn {
      background-color: rgba(3, 98, 76, 0.35) !important;
      color: rgba(255, 255, 255, 0.5) !important;

      .v-icon {
        color: rgba(255, 255, 255, 0.5) !important;
      }
    }

    .v-input input {
      font-family: 'IBM Plex Sans', 'Roboto', sans-serif;
    }
  }
</style>
